import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SharedSlotsPreview from "@/components/SharedSlotsPreview";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FileStack, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * Lets the user pick which photo templates apply to an existing project.
 * Saves via PATCH /api/projects/{id} { template_ids }. Calls onChanged() after.
 */
export default function EditTemplatesDialog({ project, onChanged }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(() => new Set(project.template_ids || []));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/templates/photos")
      .then((r) => setTemplates(r.data))
      .catch(() => setTemplates([]));
    setSelected(new Set(project.template_ids || []));
  }, [open, project.template_ids]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, { template_ids: Array.from(selected) });
      toast.success("Templates updated");
      setOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-slate-700 bg-slate-900 hover:bg-slate-800"
          data-testid="edit-templates-btn"
        >
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Templates
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <FileStack className="w-5 h-5 text-blue-400" /> Photo Templates
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Pick which templates apply to this project. Items from selected templates are merged. Adding a template adds its required photos; removing one drops those requirements (uploaded photos are kept on the project either way).
          </DialogDescription>
        </DialogHeader>

        {templates.length === 0 ? (
          <div className="text-sm text-slate-400 italic">No templates available.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            {templates.map((t) => {
              const checked = selected.has(t.id);
              const itemCount = (t.categories || []).reduce((s, c) => s + (c.items || []).length, 0);
              return (
                <label
                  key={t.id}
                  className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer ${
                    checked ? "bg-blue-950/30 border-blue-700" : "bg-slate-950 border-slate-700 hover:border-slate-600"
                  }`}
                  data-testid={`edit-tpl-${t.id}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggle(t.id)} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5 font-mono truncate">
                      {t.project_type || "any type"}
                      {t.customer ? ` · ${t.customer}` : ""}
                      {t.is_default ? " · default" : ""}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {t.categories?.length || 0} categories · {itemCount} items
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
        <SharedSlotsPreview templateIds={Array.from(selected)} testid="edit-shared-slots" />

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-red-700 hover:bg-red-600" data-testid="edit-tpl-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            SAVE TEMPLATES
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
